import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Quote } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';

/**
 * Shows a floating "Ask Risman" chip when the user selects text inside `containerRef`.
 */
export const AskRismanSelection = ({ containerRef, onAsk, enabled = true }) => {
  const { t } = useI18n();
  const [state, setState] = useState(null);

  const clear = useCallback(() => setState(null), []);

  useEffect(() => {
    if (!enabled) return undefined;

    const onSelectionChange = () => {
      const selection = window.getSelection();
      if (!selection || selection.isCollapsed || !selection.rangeCount) {
        setState(null);
        return;
      }

      const text = selection.toString().trim();
      if (!text || text.length < 2) {
        setState(null);
        return;
      }

      const range = selection.getRangeAt(0);
      const container = containerRef?.current;
      if (!container || !container.contains(range.commonAncestorContainer)) {
        setState(null);
        return;
      }

      const rect = range.getBoundingClientRect();
      if (!rect.width && !rect.height) {
        setState(null);
        return;
      }

      setState({
        text,
        top: rect.top + window.scrollY - 44,
        left: rect.left + rect.width / 2 + window.scrollX
      });
    };

    document.addEventListener('selectionchange', onSelectionChange);
    window.addEventListener('scroll', clear, true);
    return () => {
      document.removeEventListener('selectionchange', onSelectionChange);
      window.removeEventListener('scroll', clear, true);
    };
  }, [clear, containerRef, enabled]);

  if (!state || typeof document === 'undefined') return null;

  return createPortal(
    <button
      type="button"
      onMouseDown={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onAsk?.(state.text);
        window.getSelection()?.removeAllRanges();
        clear();
      }}
      className="fixed z-[70] inline-flex -translate-x-1/2 items-center gap-1.5 rounded-full border border-neutral-200 bg-white px-3 py-1.5 text-xs font-medium text-neutral-800 shadow-lg dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-100"
      style={{ top: state.top, left: state.left }}
    >
      <Quote size={13} className="opacity-70" />
      {t('chatAskRisman')}
    </button>,
    document.body
  );
};
